import PublicHeader from '../components/PublicHeader';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowRight, ArrowUpRight, Sparkle, Robot, Buildings, Lightning, ChartBar, Shield, Users, Target, FileText, Globe, CaretDown, Check, Star } from '@phosphor-icons/react';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

// Match the rest of the app: font-heading = IBM Plex Sans, font-sans = Inter
const HEAD_FONT = { fontFamily: '"IBM Plex Sans", Inter, sans-serif' };
const BODY_FONT = { fontFamily: 'Inter, "IBM Plex Sans", sans-serif' };

// =============== PRICING WIDGET ===============
const PricingWidget = () => {
  const [pricing, setPricing] = useState(null);
  const [seats, setSeats] = useState(3);

  useEffect(() => {
    api.get('/pricing/seats').then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const vcTier = useMemo(() => {
    if (!pricing) return null;
    return pricing.vc.find(t => seats >= t.min && seats <= t.max) || pricing.vc[pricing.vc.length - 1];
  }, [pricing, seats]);

  const vcTotal = vcTier ? vcTier.price_per_seat * seats : 0;

  return (
    <Tabs defaultValue="vc" className="w-full">
      <TabsList className="w-full max-w-md mx-auto bg-transparent border border-black/10 rounded-none p-1 h-auto mb-12">
        <TabsTrigger value="vc" data-testid="pricing-vc-tab" className="flex-1 rounded-none data-[state=active]:bg-black data-[state=active]:text-white text-base font-bold tracking-tight py-3" style={HEAD_FONT}>
          VC / Incubator
        </TabsTrigger>
        <TabsTrigger value="startup" data-testid="pricing-startup-tab" className="flex-1 rounded-none data-[state=active]:bg-black data-[state=active]:text-white text-base font-bold tracking-tight py-3" style={HEAD_FONT}>
          Startup
        </TabsTrigger>
      </TabsList>

      <TabsContent value="vc" className="mt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 border border-black/10">
          <div className="p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-black/10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60">Tier</span>
              <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-600 text-[11px] font-bold uppercase">{vcTier?.label || 'Solo'}</span>
            </div>
            <div className="text-6xl lg:text-7xl font-black tracking-tight text-black leading-none tabular-nums" style={HEAD_FONT}>
              ₹{vcTotal.toLocaleString('en-IN')}
            </div>
            <div className="mt-2 text-base text-black/60">
              <span className="font-bold text-black">₹{vcTier?.price_per_seat?.toLocaleString('en-IN') || '—'}</span> × <span className="font-bold text-black">{seats}</span> seat{seats === 1 ? '' : 's'} / month
            </div>
            <div className="mt-1 text-xs text-black/50">+ 18% GST · billed monthly</div>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60">Team size</div>
                <div className="text-2xl font-black text-black tabular-nums" style={HEAD_FONT}>{seats}</div>
              </div>
              <Slider
                data-testid="pricing-seats-slider"
                value={[seats]}
                onValueChange={(v) => setSeats(v[0])}
                min={1}
                max={20}
                step={1}
                className="my-4"
              />
              <div className="flex justify-between text-[10px] text-black/40 font-mono mt-2">
                <span>1</span><span>5</span><span>10</span><span>15</span><span>20+</span>
              </div>
            </div>

            <button
              onClick={() => window.location.href = '/login'}
              data-testid="pricing-vc-cta"
              className="mt-10 w-full py-4 bg-black text-white text-sm uppercase tracking-[0.15em] font-bold hover:bg-black/85 transition-colors flex items-center justify-center gap-2"
              style={HEAD_FONT}
            >
              Get Started <ArrowRight size={16} weight="bold" />
            </button>
          </div>

          <div className="p-8 lg:p-12 bg-neutral-50">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60 mb-6">All Tiers Include</div>
            <ul className="space-y-3.5 text-sm text-black/80" style={BODY_FONT}>
              {[
                'AI Co-Pilot with full portfolio context',
                'Multi-fund management & MoIC engine',
                'Unlimited programs & public apply links',
                'Drag-and-drop applicant Kanban',
                'LP-grade PDF reports',
                'Live ticker & investor activity feed',
                'Founder discovery & tracker',
              ].map(f => (
                <li key={f} className="flex items-start gap-3">
                  <Check size={16} weight="bold" className="text-black mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {pricing && (
              <div className="mt-10 pt-8 border-t border-black/10">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60 mb-4">Volume Pricing</div>
                <div className="space-y-2.5">
                  {pricing.vc.map(t => (
                    <div key={t.label} className={`flex items-center justify-between text-sm ${vcTier?.label === t.label ? 'text-black font-bold' : 'text-black/60'}`}>
                      <span>{t.label} · {t.min === t.max ? '1' : `${t.min}–${t.max === 999 ? '∞' : t.max}`} seat{t.max === 1 ? '' : 's'}</span>
                      <span className="tabular-nums">₹{t.price_per_seat.toLocaleString('en-IN')}/seat</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="startup" className="mt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 border border-black/10">
          <div className="p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-black/10 relative overflow-hidden">
            <div className="absolute top-6 right-6 px-3 py-1.5 bg-emerald-500 text-white text-[11px] font-black uppercase tracking-wider" style={HEAD_FONT}>
              FREE · 6 MONTHS
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60 mb-3">Founder Plan</div>
            <div className="text-7xl lg:text-8xl font-black tracking-tight text-black leading-none" style={HEAD_FONT}>
              ₹0
            </div>
            <div className="mt-2 text-base text-black/60">
              for the first <span className="font-bold text-black">6 months</span>, then ₹499/seat/month
            </div>
            <div className="mt-1 text-xs text-black/50">+ 18% GST · billed monthly</div>

            <div className="mt-10 grid grid-cols-3 gap-3">
              <div className="border border-black/10 p-4">
                <div className="text-3xl font-black text-emerald-600 tabular-nums" style={HEAD_FONT}>3×</div>
                <div className="text-[10px] uppercase tracking-wider text-black/50 mt-1">Acceptance rate</div>
              </div>
              <div className="border border-black/10 p-4">
                <div className="text-3xl font-black text-black tabular-nums" style={HEAD_FONT}>0</div>
                <div className="text-[10px] uppercase tracking-wider text-black/50 mt-1">Setup fees</div>
              </div>
              <div className="border border-black/10 p-4">
                <div className="text-3xl font-black text-black tabular-nums" style={HEAD_FONT}>∞</div>
                <div className="text-[10px] uppercase tracking-wider text-black/50 mt-1">Programs to apply</div>
              </div>
            </div>

            <button
              onClick={() => window.location.href = '/login'}
              data-testid="pricing-startup-cta"
              className="mt-10 w-full py-4 bg-emerald-600 text-white text-sm uppercase tracking-[0.15em] font-bold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
              style={HEAD_FONT}
            >
              Start free <ArrowRight size={16} weight="bold" />
            </button>
          </div>

          <div className="p-8 lg:p-12 bg-neutral-50">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60 mb-6">Everything You Get</div>
            <ul className="space-y-3.5 text-sm text-black/80" style={BODY_FONT}>
              {[
                'AI Coach with full context (invites, programs, runway)',
                'Monthly metrics dashboard with 6 charts',
                'Sector & stage benchmarks vs. peers',
                'One-click apply to investor programs',
                'CA, legal, registrations marketplace',
                'Pitch deck + monthly MIS storage',
                'VC invitations & MoIC visibility',
              ].map(f => (
                <li key={f} className="flex items-start gap-3">
                  <Check size={16} weight="bold" className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 pt-8 border-t border-black/10 text-xs text-black/60" style={BODY_FONT}>
              <strong className="text-black">No credit card required.</strong> Invite co-founders, advisors, or your CFO at ₹499/seat — same generous free trial applies.
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
};

// =============== HERO MOCKED AI BUBBLE ===============
const FloatingAIMock = () => (
  <div className="absolute -bottom-4 -right-2 sm:-bottom-6 sm:-right-6 lg:-bottom-12 lg:-right-12 w-[240px] sm:w-[280px] lg:w-[340px] bg-white border border-black/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)]" style={BODY_FONT}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 bg-gradient-to-r from-indigo-50 to-transparent">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-indigo-500/15 flex items-center justify-center">
          <Robot size={15} weight="fill" className="text-indigo-600" />
        </div>
        <div>
          <div className="text-[12px] font-bold text-black tracking-tight">AI Co-Pilot</div>
          <div className="text-[9px] text-black/40 uppercase tracking-wider">Live · Lab32 Cohort 14</div>
        </div>
      </div>
      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
    </div>
    <div className="p-4 space-y-3 max-h-[280px] overflow-hidden">
      <div className="text-[12px] text-black/80 leading-relaxed">
        Top 2 best-fit applicants for <strong>Lab32 Cohort 14</strong>:
      </div>
      <div className="text-[12px] text-black leading-relaxed pl-3 border-l-2 border-indigo-500">
        <strong>Vaultik</strong> · Fintech, Seed · ₹89K MRR<br/>
        <strong>GreenGrid</strong> · CleanTech · ₹3L MRR
      </div>
      <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-500/20">
        <Sparkle size={11} weight="fill" /> Move to Accepted <ArrowRight size={10} weight="bold" />
      </button>
    </div>
  </div>
);

// =============== PERSONA TABS WITH MOCK UIs ===============
const PersonaShowcase = () => {
  const personas = {
    vc: {
      label: 'VCs',
      color: 'indigo-500',
      colorClass: 'text-indigo-500',
      bg: 'bg-indigo-50',
      features: [
        ['Robot', 'AI Co-Pilot with full portfolio context'],
        ['ChartBar', 'Multi-fund MoIC engine'],
        ['Lightning', 'Live ticker of investor activity'],
        ['Buildings', 'Multi-fund management'],
        ['Globe', 'Watchlists with public apply links'],
        ['Target', 'Drag-and-drop applicant Kanban'],
        ['FileText', 'LP-ready PDF reports'],
      ],
    },
    incubator: {
      label: 'Incubators',
      color: 'amber-500',
      colorClass: 'text-amber-500',
      bg: 'bg-amber-50',
      features: [
        ['Globe', 'Programs with public apply pages'],
        ['Lightning', 'OG meta-tag share links for LinkedIn'],
        ['Target', 'Cohort kanban: FULL vs QUICK profiles'],
        ['Robot', 'AI shortlisting — "rank top 10 applicants"'],
        ['Sparkle', 'One-click bulk move actions'],
      ],
    },
    startup: {
      label: 'Startups',
      color: 'emerald-500',
      colorClass: 'text-emerald-500',
      bg: 'bg-emerald-50',
      features: [
        ['ChartBar', 'Monthly metrics dashboard (6 charts)'],
        ['Robot', 'AI Coach with full context: invites, programs, runway'],
        ['Target', 'Sector & stage benchmarks vs. peers'],
        ['Globe', 'Discover & one-click apply to programs'],
        ['Buildings', 'Services marketplace — CA, legal, registrations'],
      ],
    },
  };

  const iconMap = { Robot, ChartBar, Lightning, Buildings, Globe, Target, FileText, Sparkle };

  return (
    <Tabs defaultValue="vc" className="w-full">
      <TabsList className="w-full bg-transparent border-b border-black/10 rounded-none p-0 h-auto justify-start gap-0 mb-12">
        {Object.entries(personas).map(([key, p]) => (
          <TabsTrigger
            key={key}
            value={key}
            data-testid={`persona-tab-${key}`}
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 sm:px-6 py-3 sm:py-4 text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-black/30 data-[state=active]:text-black hover:text-black/70 transition-colors"
            style={HEAD_FONT}
          >
            {p.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {Object.entries(personas).map(([key, p]) => (
        <TabsContent key={key} value={key} className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left: features */}
            <div className="lg:col-span-5">
              <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${p.colorClass} mb-4`}>For {p.label}</div>
              <h3 className="text-3xl lg:text-4xl font-black tracking-tight text-black mb-8" style={HEAD_FONT}>
                Everything {p.label === 'Startups' ? 'a founder' : p.label === 'Incubators' ? 'an incubator manager' : 'a fund manager'} runs in a week — in one place.
              </h3>
              <ul className="space-y-4" style={BODY_FONT}>
                {p.features.map(([icn, text]) => {
                  const Ic = iconMap[icn] || Sparkle;
                  return (
                    <li key={text} className="flex items-start gap-4 group">
                      <div className={`w-8 h-8 ${p.bg} border border-black/10 flex items-center justify-center flex-shrink-0`}>
                        <Ic size={14} className={p.colorClass} weight="bold" />
                      </div>
                      <span className="text-base text-black/80 pt-1">{text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Right: mock UI based on persona */}
            <div className="lg:col-span-7 border border-black/10 bg-white">
              {key === 'vc' && <KanbanMock />}
              {key === 'incubator' && <RankingMock />}
              {key === 'startup' && <DashboardMock />}
            </div>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};

// Static mocks (pure tailwind, no app imports)
const KanbanMock = () => (
  <div className="p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/60">Lab32 Cohort 14 · 32 applicants</div>
      <div className="flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3" style={BODY_FONT}>
      {[
        { title: 'Received', count: 18, items: [['Vaultik','Fintech','full'], ['Frontend Tester','—','quick'], ['NourishBox','D2C','full']] },
        { title: 'Accepted', count: 8, items: [['CodePilot','Deep Tech / AI','full'], ['MedLync','Health Tech','full']], tone: 'emerald' },
        { title: 'Rejected', count: 6, items: [['LogiTrack','Logistics','full']], tone: 'rose' },
      ].map((col, i) => (
        <div key={i} className={`border border-black/10 ${col.tone === 'emerald' ? 'bg-emerald-50/50' : col.tone === 'rose' ? 'bg-rose-50/50' : 'bg-neutral-50/50'} p-3`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`text-[10px] uppercase tracking-wider font-bold ${col.tone === 'emerald' ? 'text-emerald-700' : col.tone === 'rose' ? 'text-rose-700' : 'text-black/60'}`}>{col.title}</div>
            <div className="text-[10px] font-mono text-black/40">{col.count}</div>
          </div>
          <div className="space-y-2">
            {col.items.map(([name, sector, type], j) => (
              <div key={j} className="bg-white border border-black/10 p-2.5">
                <div className="text-[12px] font-bold text-black truncate">{name}</div>
                <div className="text-[10px] text-black/50">{sector}</div>
                <span className={`mt-1 inline-block text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 ${type === 'full' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-black/5 text-black/40'}`}>
                  {type === 'full' ? 'Full' : 'Quick'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const RankingMock = () => (
  <div className="p-5" style={BODY_FONT}>
    <div className="flex items-center gap-2 mb-4">
      <Robot size={14} weight="fill" className="text-amber-500" />
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-600">AI shortlist · "Top 5 for SaaS Bootcamp"</div>
    </div>
    <div className="space-y-2">
      {[
        ['Vaultik', 'Fintech · Seed', 'A+', 'Strong sector match · Rs.89K MRR'],
        ['CodePilot', 'AI Dev Tools · Seed', 'A', 'Thesis-aligned · 22% MoM growth'],
        ['SkillForge', 'EdTech · Pre-seed', 'A−', 'Adjacent sector · Strong team'],
        ['NourishBox', 'D2C · Seed', 'B+', 'Off-thesis but solid metrics'],
        ['PayLoop', 'B2B SaaS · Pre-seed', 'B', 'Early but founder market fit clear'],
      ].map(([name, meta, grade, note], i) => (
        <div key={name} className="grid grid-cols-12 gap-3 items-center border border-black/10 p-3 hover:bg-amber-50/40 transition-colors">
          <div className="col-span-1 text-2xl font-black tabular-nums text-black" style={HEAD_FONT}>{i+1}</div>
          <div className="col-span-5 min-w-0">
            <div className="text-sm font-bold text-black truncate">{name}</div>
            <div className="text-[11px] text-black/50">{meta}</div>
          </div>
          <div className="col-span-1 text-xl font-black text-amber-600" style={HEAD_FONT}>{grade}</div>
          <div className="col-span-5 text-[11px] text-black/60 truncate">{note}</div>
        </div>
      ))}
    </div>
    <button className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 text-[11px] font-bold uppercase tracking-wider">
      <Sparkle size={11} weight="fill" /> Accept top 3 <ArrowRight size={10} weight="bold" />
    </button>
  </div>
);

const DashboardMock = () => (
  <div className="p-5" style={BODY_FONT}>
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-base font-black text-black tracking-tight" style={HEAD_FONT}>Vaultik · Fintech</div>
        <div className="text-[10px] uppercase tracking-wider text-black/50">Founder dashboard · Feb 2026</div>
      </div>
      <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 bg-emerald-500/10 px-2 py-1">15.5mo runway</span>
    </div>
    <div className="grid grid-cols-3 gap-3 mb-4">
      {[['MRR', '₹89K', '+22%'], ['Cash', '₹1.4Cr', '−2%'], ['Burn', '₹91K/mo', 'flat']].map(([l,v,d]) => (
        <div key={l} className="border border-black/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-black/50">{l}</div>
          <div className="text-xl font-black text-black tabular-nums mt-1" style={HEAD_FONT}>{v}</div>
          <div className={`text-[10px] font-mono ${d.startsWith('+') ? 'text-emerald-600' : d.startsWith('−') ? 'text-rose-600' : 'text-black/40'}`}>{d}</div>
        </div>
      ))}
    </div>
    {/* Mini bar chart */}
    <div className="border border-black/10 p-3">
      <div className="text-[10px] uppercase tracking-wider text-black/50 mb-3">MRR · last 6 months</div>
      <div className="flex items-end gap-1.5 h-20">
        {[42, 51, 48, 63, 71, 89].map((h, i) => (
          <div key={i} className="flex-1 bg-emerald-500/80 hover:bg-emerald-500 transition-colors" style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[9px] text-black/40 font-mono">
        <span>SEP</span><span>OCT</span><span>NOV</span><span>DEC</span><span>JAN</span><span>FEB</span>
      </div>
    </div>
  </div>
);

// =============== MAIN LANDING PAGE ===============
const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-black" style={BODY_FONT}>
      {/* ============ STICKY NAV ============ */}
      <PublicHeader />

      {/* ============ HERO ============ */}
      <section className="pt-24 sm:pt-28 pb-16 sm:pb-20 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-6">
              Built for the Indian Capital Ecosystem
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] tracking-tight text-black" style={HEAD_FONT}>
              Scale your<br />
              capital engine.<br />
              <span className="text-black/40">Without the friction.</span>
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-black/70 leading-relaxed max-w-xl" style={BODY_FONT}>
              The unified platform for VCs, Incubators, and Startups. Move faster with AI-native workflows, multi-fund management, and automated reporting — all in ₹.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
              <button onClick={() => navigate('/login', { state: { mode: 'signup' } })} data-testid="hero-cta-primary"
                className="px-6 sm:px-7 py-3.5 sm:py-4 bg-black text-white text-xs sm:text-sm uppercase tracking-[0.15em] font-bold hover:bg-black/85 transition-colors flex items-center gap-2"
                style={HEAD_FONT}>
                Get started <ArrowRight size={16} weight="bold" />
              </button>
              <a href="#personas" data-testid="hero-cta-secondary"
                className="px-6 sm:px-7 py-3.5 sm:py-4 border border-black text-black text-xs sm:text-sm uppercase tracking-[0.15em] font-bold hover:bg-black hover:text-white transition-colors flex items-center gap-2"
                style={HEAD_FONT}>
                See it in action
              </a>
            </div>
            <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-black/50" style={BODY_FONT}>
              <div className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> Free 6 months for startups</div>
              <div className="flex items-center gap-2"><Check size={14} weight="bold" className="text-emerald-600" /> No setup fees</div>
            </div>
          </div>

          {/* Right: layered visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative border border-black/10 bg-white aspect-[4/5] overflow-hidden">
              <div className="absolute inset-0 grid grid-cols-12 gap-px opacity-[0.03] pointer-events-none">
                {[...Array(144)].map((_, i) => <div key={i} className="bg-black" />)}
              </div>
              <KanbanMock />
            </div>
            <FloatingAIMock />
          </div>
        </div>
      </section>

      {/* ============ PERSONA SHOWCASE ============ */}
      <section id="personas" className="py-16 sm:py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-12 sm:mb-16">
            <div className="lg:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-4">Three personas, one platform</div>
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tight leading-[1] text-black" style={HEAD_FONT}>
                Built deeply<br />for each side<br />of the table.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-base sm:text-lg text-black/60 leading-relaxed" style={BODY_FONT}>
                VCs run their portfolio. Incubators manage cohorts. Startups raise capital and grow.
                Founderboard is the single connective tissue — purpose-built UI for each role, sharing the same source of truth.
              </p>
            </div>
          </div>
          <PersonaShowcase />
        </div>
      </section>

      {/* ============ AI EVERYWHERE ============ */}
      <section id="ai" className="bg-[#0A0A0A] text-white py-16 sm:py-24 lg:py-36 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-indigo-400 mb-6">AI · Everywhere</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-end mb-12 sm:mb-16">
            <h2 className="lg:col-span-7 text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[0.95]" style={HEAD_FONT}>
              Context-aware AI<br />at every step.
            </h2>
            <p className="lg:col-span-5 text-base sm:text-lg text-white/60 leading-relaxed" style={BODY_FONT}>
              Not another chatbot. The AI Bubble understands your active pipeline, portfolio data, runway, applications and answers — and acts on your behalf with one-click confirmations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-6 border border-white/15 p-8 lg:p-10">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-400 mb-4">For VCs / Incubators</div>
              <div className="text-2xl font-black tracking-tight mb-4" style={HEAD_FONT}>"Shortlist top 10 applicants for Lab32 Cohort 14"</div>
              <div className="text-sm text-white/60 mb-6 leading-relaxed">AI ranks every applicant by thesis fit, traction & profile depth — emits a one-click <span className="text-emerald-400 font-bold">Move 10 to Accepted</span> button right inside the chat.</div>
              <div className="flex flex-wrap gap-2">
                <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-white/5 border border-white/15">Bulk-move</span>
                <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-white/5 border border-white/15">Reply drafts</span>
                <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-white/5 border border-white/15">Deck eval</span>
              </div>
            </div>
            <div className="lg:col-span-6 border border-white/15 p-8 lg:p-10">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400 mb-4">For Startups</div>
              <div className="text-2xl font-black tracking-tight mb-4" style={HEAD_FONT}>"Should I take the Test VC Fund offer?"</div>
              <div className="text-sm text-white/60 mb-6 leading-relaxed">AI Coach pulls your real cap table math, runway, sector benchmarks and recent invites — gives you a direct, founder-honest answer in 3 sentences.</div>
              <div className="flex flex-wrap gap-2">
                <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-white/5 border border-white/15">Cap table maths</span>
                <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-white/5 border border-white/15">Runway</span>
                <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-white/5 border border-white/15">Term-sheet review</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURE GRID (BENTO) ============ */}
      <section className="py-16 sm:py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-4">The full stack</div>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tight leading-[1] text-black mb-12 sm:mb-16 max-w-3xl" style={HEAD_FONT}>
            Every workflow you<br/>currently glue together<br />in spreadsheets.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {[
              { icon: ChartBar, t: 'Multi-fund MoIC', d: 'Round-by-round investment math, equity dilution, blended fund MoIC.' },
              { icon: Buildings, t: 'Programs & cohorts', d: 'Public apply pages, share links with rich LinkedIn previews.' },
              { icon: Robot, t: 'AI Co-Pilot', d: 'Sees every tab. Acts with one-click bulk operations.' },
              { icon: Users, t: 'Team seats', d: 'Owner / Admin / Member roles. Volume seat pricing.' },
              { icon: FileText, t: 'LP-ready reports', d: 'PDF exports, monthly MIS digests, customisable templates.' },
              { icon: Star, t: 'Discover & tracker', d: 'Browse startups, save to tracker, request decks privately.' },
              { icon: Lightning, t: 'Live ticker', d: 'Real-time activity feed across your portfolio.' },
              { icon: Shield, t: 'Verified rounds', d: 'Two-sided invite + accept. Tamper-proof MoIC.' },
              { icon: Globe, t: 'Services marketplace', d: 'Founder-curated CA / legal / registration vendors.' },
            ].map(({ icon: I, t, d }) => (
              <div key={t} className="bg-white p-8 lg:p-10 hover:bg-neutral-50 transition-colors">
                <I size={20} weight="bold" className="text-black mb-5" />
                <div className="text-lg font-black tracking-tight text-black mb-2" style={HEAD_FONT}>{t}</div>
                <div className="text-sm text-black/60 leading-relaxed" style={BODY_FONT}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="pricing" className="bg-neutral-50 py-16 sm:py-24 lg:py-36 border-y border-black/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-4">Pricing</div>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tight leading-[1] text-black mb-4 max-w-3xl" style={HEAD_FONT}>
            Volume pricing.<br/>Founder-friendly.
          </h2>
          <p className="text-base sm:text-lg text-black/60 mb-12 sm:mb-16 max-w-2xl" style={BODY_FONT}>
            Pay only for the team you bring on. Startups get the first 6 months free.
          </p>
          <PricingWidget />
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq" className="py-16 sm:py-24 lg:py-36">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-4">FAQ</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1] text-black mb-10 sm:mb-12" style={HEAD_FONT}>
            Questions, answered.
          </h2>
          <Accordion type="single" collapsible className="border-t border-black/10">
            {[
              ['Who is Founderboard for?', 'VCs, Incubators, Accelerators, Angel groups, and the startups they back. Each persona has a purpose-built UI, but everyone shares the same source of truth.'],
              ['Is the AI an add-on or included?', 'Included. Every plan ships with the full AI Co-Pilot or AI Coach — no add-on, no per-message metering.'],
              ['How does seat pricing actually work?', 'Owners and Admins manage seats from the Team tab. Add a seat, invite by email, role-assign. Volume tiers kick in automatically: 2-5 seats at ₹3,499 each, 6+ seats at ₹2,999 each. All prices exclude GST.'],
              ['Do startups really get 6 months free?', 'Yes. Sign up as a founder and use everything — dashboard, AI Coach, programs, services — free for 6 months. Then ₹499/seat/month.'],
              ['Can I export my data?', 'Yes. Portfolio CSV, monthly MIS Excel, LP-ready PDFs — all one click. We never lock data in.'],
              ['How is this different from Affinity / Airtable?', 'They are CRMs you have to configure. We are an opinionated workflow built for the Indian early-stage ecosystem — public apply pages, MoIC, GST-aware pricing, AI that knows your applications natively.'],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`q${i}`} data-testid={`faq-q-${i}`} className="border-b border-black/10">
                <AccordionTrigger className="text-left text-lg lg:text-xl font-bold tracking-tight text-black hover:no-underline py-6" style={HEAD_FONT}>
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-black/70 leading-relaxed pb-6" style={BODY_FONT}>{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-black text-white py-16 sm:py-24 lg:py-36 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '0 48px' }} />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative">
          <h2 className="text-4xl sm:text-5xl lg:text-8xl font-black tracking-tight leading-[0.92] max-w-5xl" style={HEAD_FONT}>
            Stop managing<br />spreadsheets.<br />
            <span className="text-white/40">Start managing capital.</span>
          </h2>
          <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-3 sm:gap-4">
            <button onClick={() => navigate('/login', { state: { mode: 'signup' } })} data-testid="cta-final-primary"
              className="px-6 sm:px-8 py-4 sm:py-5 bg-white text-black text-xs sm:text-sm uppercase tracking-[0.15em] font-bold hover:bg-white/85 flex items-center gap-2"
              style={HEAD_FONT}>
              Get started <ArrowUpRight size={16} weight="bold" />
            </button>
            <button onClick={() => navigate('/login')} data-testid="cta-final-secondary"
              className="px-6 sm:px-8 py-4 sm:py-5 border border-white text-white text-xs sm:text-sm uppercase tracking-[0.15em] font-bold hover:bg-white hover:text-black"
              style={HEAD_FONT}>
              Log in
            </button>
          </div>
          <div className="mt-16 pt-10 border-t border-white/15 flex flex-wrap justify-between gap-6 text-xs text-white/50" style={BODY_FONT}>
            <span>© Founderboard 2026 · Built in India</span>
            <span className="flex gap-6">
              <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy</a>
              <a href="/terms" data-testid="footer-terms-link" className="hover:text-white">Terms</a>
              <a href="mailto:sid@founderboard.io" className="hover:text-white">sid@founderboard.io</a>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
